import styled from 'styled-components';
import React, { Dispatch, SyntheticEvent } from 'react';
import {
    ContentLoader,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    DotsIcon,
    FilterIcon,
    formatNumber,
    MobileCardRowsProps,
    palette,
    SimplePopover as Popover,
    SimplePopoverPlainOption,
    TemplatePreview,
    TertiaryButton,
    TertiaryButtonGroup,
    Typography,
} from '@dev/ee-components';
import { map, truncate } from 'lodash-es';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { AccessLevel, Campaign, CampaignStatus, CampaignType } from '@dev/ee-api/ee-api-models';
import { push as Push } from 'redux-first-history';
import i18n from '@/../i18n';

import { getApiPath } from '@/services/Secuirty';
import { CampaignsInterface } from '@/store/campaigns/types';
import { DropdownOptions } from '@/store/campaigns/types';
import { CancelCampaign, CopyCampaignsByNameStart, DeleteCampaigns, PreviewCampaign } from '@/store/campaigns/actions';
import { CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { getCampaignCreatorType } from '@/services/tools/getCampaignCreatorType';
import { ConfigState } from '@/store/config/types';
import { ShowDialog } from '@/store/dialog/actions';

import CampaignsListFilterItem from './components/CampaignsListFilterItem';
import { isCampaignsReportAvailable, isCancelCampaignPossible } from '..';
import {
    CampaignStatusExtended,
    getMobileDateRangeOptions,
    getMobileStatusOptions,
    getStatusOptions,
    MobileFilterOption,
    screenCampaignTypes,
    getCampaignTypeOptions
} from './config';
import { StatusBadgeComponent } from './components/StatusBadge';
import { CampaignName } from './components/CampaignName';
import { isFutureScheduledWithAnyDelivered } from '../helperFunctions';

export const getCampaignsListEmptyRows = (isMobile: boolean, itemsCount = 3): CustomTableRowsProps[] => {
    const rows: CustomTableRowsProps[] = [];
    let index;

    for (index = 0; index < itemsCount; index++) {
        if (isMobile) {
            rows.push({
                tableTitle: <ContentLoader mode="row" />,
                audience: <ContentLoader mode="row" />,
                status: <ContentLoader mode="row" />,
                delivered: <ContentLoader mode="row" />,
                opened: <ContentLoader mode="row" />,
                clicked: <ContentLoader mode="row" />,
            });
        } else {
            rows.push({
                preview: <ContentLoader mode="row" height={70} width={70} />,
                campaignName: (
                    <>
                        <div style={{ marginBottom: '5px' }}>
                            <ContentLoader mode="row" height={15} />
                        </div>
                        <ContentLoader mode="row" height={15} />
                    </>
                ),
                delivered: <ContentLoader mode="row" height={20} />,
                opened: <ContentLoader mode="row" height={20} />,
                clicked: <ContentLoader mode="row" height={20} />,
                spaceColumn: <ContentLoader mode="row" height={20} />,
                status: [
                    <ContentLoader mode="row" height={20} key="skeleton" />,
                    <ContentLoader mode="row" height={20} key="skeleton" />,
                ],
                viewReport: <ContentLoader mode="row" height={20} />,
                rowID: index.toString(),
            });
        }
    }

    return rows;
};

const haveAccessFor = (accuntAccessLevels: AccessLevel[], access: AccessLevel): boolean =>
    accuntAccessLevels?.includes?.(access);

type OnFilterStatusClickHandler = (
    event: SyntheticEvent<HTMLInputElement>,
    checkedStatus: CampaignStatusExtended[]
) => void;

const getCustomDropdownBody = (element: { label: string; count: number }): JSX.Element => (
    <CampaignsListFilterItem label={element.label} count={element.count} />
);

const getStatusFilters = (
    campaigns: CampaignsInterface,
    defaultValues: string[],
    onFilterClick: OnFilterStatusClickHandler
): JSX.Element => {
    const statusOptions = getStatusOptions(campaigns);

    if (statusOptions.length > 1) {
        return (
            <Popover
                type="icon"
                options={statusOptions}
                enableCheckbox={true}
                defaultCheckboxValue={defaultValues}
                customDropdownBody={getCustomDropdownBody}
                onCheckboxClick={onFilterClick}
                popperProps={{
                    disablePortal: false,
                }}
            >
                <FilterIcon color="#708097" />
            </Popover>
        );
    }
};
interface GetTableHeadersProps {
    campaigns: CampaignsInterface;
    selectedStatusFilters: string[];
    onFilterClick: OnFilterStatusClickHandler;
    accountAccessLevels: AccessLevel[];
    onExportCampaignsClick: () => void;
    onDeleteCampaignsClick: () => void;
}

export const getTableHeaders = ({
    campaigns,
    accountAccessLevels,
    selectedStatusFilters,
    onFilterClick,
    onExportCampaignsClick,
    onDeleteCampaignsClick,
}: GetTableHeadersProps): CustomTableHeadersProps[] => {
    if (campaigns.checkedCampaigns.length) {
        return [
            {
                id: 'preview',
                numeric: false,
                label: i18n.t('campaigns'),
            },
            {
                id: 'campaignName',
                numeric: false,
                label: '',
            },
            {
                id: 'delivered',
                numeric: false,
                label: '',
                headerAlign: 'right',
            },
            {
                id: 'opened',
                numeric: false,
                label: '',
                headerAlign: 'right',
            },
            {
                id: 'clicked',
                numeric: false,
                label: '',
                headerAlign: 'right',
            },
            { id: 'spaceColumn', numeric: false, label: '' },
            {
                id: 'status',
                numeric: false,
                label: '',
            },
            {
                id: 'viewReport',
                numeric: false,
                label: '',
                headerAlign: 'right',
                columnAlign: 'center',
                filterIcon: (
                    <TertiaryButtonGroup>
                        {haveAccessFor(accountAccessLevels, AccessLevel.Export) && (
                            <TertiaryButton
                                style={{ margin: 0, color: palette.indigo[6] }}
                                text={i18n.t('export')}
                                onClick={onExportCampaignsClick}
                            />
                        )}
                        {haveAccessFor(accountAccessLevels, AccessLevel.ModifyCampaigns) && (
                            <TertiaryButton
                                style={{ margin: 0, color: palette.red[6] }}
                                text={i18n.t('delete')}
                                onClick={onDeleteCampaignsClick}
                            />
                        )}
                    </TertiaryButtonGroup>
                ),
            },
        ];
    }

    const statusFilters = getStatusFilters(campaigns, selectedStatusFilters, onFilterClick);

    return [
        { id: 'preview', numeric: false, label: '' },
        { id: 'campaignName', numeric: false, label: i18n.t('name') },
        { id: 'delivered', numeric: false, label: i18n.t('delivered'), headerAlign: 'right' },
        { id: 'opened', numeric: false, label: i18n.t('opened'), headerAlign: 'right' },
        { id: 'clicked', numeric: false, label: i18n.t('clicked'), headerAlign: 'right' },
        { id: 'spaceColumn', numeric: false, label: '' },
        { id: 'status', numeric: false, label: i18n.t('status'), filterIcon: statusFilters },
        { id: 'viewReport', numeric: false, headerAlign: 'right', columnAlign: 'center' },
    ];
};

const TableItemButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    padding-left: 25px;
    align-items: center;

    .MuiTypography-body1 a.MuiLink-root {
        word-break: break-all;
        white-space: normal;
    }
`;

export const rowDropdownOptionsHandler = (
    dispatch: Dispatch<any>,
    config: ConfigState,
    option: { label: string; value: string },
    campaign?: Campaign
): void => {
    switch (option.value) {
        case DropdownOptions.Edit:
            if (config.isStandalone) {
                const campaignCreatorType = getCampaignCreatorType(campaign.Type);
                dispatch(
                    Push(
                        `${CampaignsPaths.CampaignCreatorName.replace(pathsParams.type, campaignCreatorType).replace(
                            pathsParams.name,
                            safelyEncodeBase64Url(campaign.Name)
                        )}`
                    )
                );
            } else {
                window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
                    ':name',
                    safelyEncodeBase64Url(campaign.Name)
                )}`;
            }
            break;

        case DropdownOptions.CancelSending:
            dispatch(CancelCampaign(campaign));
            break;
        case DropdownOptions.CopyCampaign: {
            dispatch(
                ShowDialog({
                    title: i18n.t('copycampaign'),
                    body: `${i18n.t('copycampaignconfirmation')} "${campaign.Name}"?`,
                    confirmLabel: i18n.t('copy'),
                    cancelLabel: i18n.t('cancel'),
                    onClickConfirm: () => dispatch(CopyCampaignsByNameStart(campaign.Name)),
                })
            );
            break;
        }
        case DropdownOptions.Delete:
            dispatch(DeleteCampaigns([campaign.Name], screenCampaignTypes));
            break;
        case DropdownOptions.ViewDetails:
            if (config.isStandalone) {
                const campaignCreatorType = getCampaignCreatorType(campaign.Type);
                dispatch(
                    Push(
                        `${CampaignsPaths.CampaignCreatorDetails.replace(pathsParams.type, campaignCreatorType).replace(
                            pathsParams.name,
                            safelyEncodeBase64Url(campaign.Name)
                        )}`
                    )
                );
            } else {
                window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
                    ':name',
                    safelyEncodeBase64Url(campaign.Name)
                )}`;
            }
            break;
        default:
            break;
    }
};

export const goToCampaign = (
    dispatch: Dispatch<any>,
    config: ConfigState,
    singleCampaign: Campaign,
    onlyDetails?: boolean
): void => {
    const isReportAvailable = isCampaignsReportAvailable(singleCampaign);
    if (isReportAvailable && !onlyDetails) {
        const uri = (
            singleCampaign.Type === CampaignType.AbSplit
                ? CampaignsPaths.CampaignAbResults
                : CampaignsPaths.CampaignOverview
        )
            .replace(':type', singleCampaign.Type)
            .replace(':name', safelyEncodeBase64Url(singleCampaign.Name));
        return dispatch(Push(uri));
    }

    if (config.isStandalone) {
        const campaignCreatorType = getCampaignCreatorType(singleCampaign.Type);
        const path =
            !isReportAvailable && !onlyDetails
                ? CampaignsPaths.CampaignCreatorName
                : CampaignsPaths.CampaignCreatorDetails;
        dispatch(
            Push(
                `${path
                    .replace(pathsParams.type, campaignCreatorType)
                    .replace(pathsParams.name, safelyEncodeBase64Url(singleCampaign.Name))}`
            )
        );
    } else {
        top.window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
            ':name',
            safelyEncodeBase64Url(singleCampaign.Name)
        )}`;
    }
};

export const getCampaignsListRows = (
    dispatch: Dispatch<any>,
    config: ConfigState,
    campaigns: CampaignsInterface
): CustomTableRowsProps[] =>
    map(campaigns?.collection, (singleCampaign: Campaign, index: number): CustomTableRowsProps => {
        const rowPopoverOptions = [
            ...(singleCampaign.Status !== CampaignStatus.Active
                ? [
                      {
                          label: i18n.t('viewdetails'),
                          value: DropdownOptions.ViewDetails,
                      },
                  ]
                : []),
            ...(isFutureScheduledWithAnyDelivered(singleCampaign)
                ? [
                      {
                          label: i18n.t('edit'),
                          value: DropdownOptions.Edit,
                      },
                  ]
                : []),
            {
                label: i18n.t('copycampaign'),
                value: DropdownOptions.CopyCampaign,
            },
            {
                label: i18n.t('delete'),
                value: DropdownOptions.Delete,
                color: palette.red[6],
            },
            ...(isCancelCampaignPossible(singleCampaign)
                ? [
                      {
                          label: i18n.t('cancelsending'),
                          value: DropdownOptions.CancelSending,
                      },
                  ]
                : []),
        ];

        const campaignTemplatesThumbnail = singleCampaign?.CampaignTemplates[0]?.MediumScreenshot
            ? `${getApiPath()}/userfile${singleCampaign.CampaignTemplates[0].MediumScreenshot}`
            : '';
        const campaignRecipents = [
            ...(singleCampaign?.Targets?.ListNames || []),
            ...(singleCampaign?.Targets?.SegmentNames || []),
        ].join(', ');

        return {
            preview: (
                <div
                    onClick={(ev): void => {
                        ev.stopPropagation();
                    }}
                >
                    <TemplatePreview
                        split={singleCampaign.CampaignTemplates.length > 1}
                        screenshot={campaignTemplatesThumbnail}
                        onClick={(): void => {
                            dispatch(PreviewCampaign(singleCampaign));
                        }}
                    />
                </div>
            ),
            campaignName: <CampaignName singleCampaign={singleCampaign} campaignRecipents={campaignRecipents} />,
            delivered: singleCampaign.Stats.Delivered,
            opened: singleCampaign?.TrackingOptions?.TrackOpens ? (
                singleCampaign?.Stats?.Opened
            ) : (
                <Typography variant="body1" align="right">
                    {i18n.t('na')}
                </Typography>
            ),
            clicked: singleCampaign?.TrackingOptions?.TrackClicks ? (
                singleCampaign.Stats.Clicked
            ) : (
                <Typography variant="body1" align="right">
                    {i18n.t('na')}
                </Typography>
            ),
            spaceColumn: '',
            status: [
                <StatusBadgeComponent status={singleCampaign.Status} index={index} key={index} />,
                <StatusBadgeComponent status={singleCampaign.Status} index={index} key={index} />,
            ],
            viewReport: (
                <TableItemButtonsWrapper>
                    <div
                        onClick={(ev): void => {
                            ev.stopPropagation();
                            goToCampaign(dispatch, config, singleCampaign);
                        }}
                    >
                        <Typography variant="body1" bodyVariant="link">
                            {isCampaignsReportAvailable(singleCampaign) ? i18n.t('viewreport') : i18n.t('edit')}
                        </Typography>
                    </div>

                    <div
                        onClick={(ev): void => {
                            ev.stopPropagation();
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <Popover
                            type="icon"
                            options={rowPopoverOptions}
                            onItemClick={(ev, option: { label: string; value: string }): void => {
                                rowDropdownOptionsHandler(dispatch, config, option, singleCampaign);
                            }}
                        >
                            <DotsIcon />
                        </Popover>
                    </div>
                </TableItemButtonsWrapper>
            ),
            rowID: singleCampaign.Name,
        };
    });

interface FilterPopoverTab {
    label: string;
    type: 'checkbox' | 'radio' | 'date';
    defaultValue?: string;
    defaultCheckboxValue?: string[];
    options: MobileFilterOption[];
    startDate?: string;
    endDate?: string;
}

export const getMobileFilterTabs = (
    campaigns: CampaignsInterface,
    defaultDateRange: string,
    defaultStatuses: string[],
    urlType: string[],
    startDate?: string,
    endDate?: string
): FilterPopoverTab[] => {
    const statusOptions = getMobileStatusOptions(campaigns);
    const typesOptions = getCampaignTypeOptions();
    const dateRangeOptions = getMobileDateRangeOptions();

    return [
        {
            label: i18n.t('daterange'),
            defaultValue: defaultDateRange,
            options: dateRangeOptions,
            type: 'date',
            startDate,
            endDate,
        },
        {
            defaultCheckboxValue: defaultStatuses,
            label: i18n.t('status'),
            options: statusOptions,
            type: 'checkbox',
        },
        {
            defaultCheckboxValue: urlType,
            label: i18n.t('type'),
            options: typesOptions,
            type: 'checkbox',
        },
    ];
};

export const getMobileTableHeaders = (): string[] => [
    i18n.t('triggeraudience'),
    i18n.t('status'),
    i18n.t('delivered'),
    i18n.t('opened'),
    i18n.t('clicked'),
];

export const getMobileTableRows = (campaigns: CampaignsInterface): MobileCardRowsProps[] =>
    map(campaigns?.collection, (campaign): MobileCardRowsProps => {
        let audienceArray = [];
        if (campaign.Targets.ListNames && campaign.Targets.SegmentNames) {
            audienceArray = campaign.Targets.ListNames.concat(campaign.Targets.SegmentNames);
        } else if (campaign.Targets.ListNames) {
            audienceArray = campaign.Targets.ListNames;
        } else if (campaign.Targets.SegmentNames) {
            audienceArray = campaign.Targets.SegmentNames;
        }

        const audience = audienceArray.join(', ');

        return {
            tableTitle: truncate(campaign.Name, { length: 35, omission: '...' }),
            Audience: audience,
            Status: <StatusBadgeComponent status={campaign.Status} index={campaign.Name} />,
            Delivered: <>{formatNumber(campaign.Stats.Delivered)}</>,
            Opened: <>{formatNumber(campaign.Stats.Opened)}</>,
            Clicked: <>{formatNumber(campaign.Stats.Clicked)}</>,
            returnOnItemClick: campaign as any,
            rowButtonText: isCampaignsReportAvailable(campaign) ? i18n.t('viewreports') : i18n.t('edit'),
        };
    });

export const getMobileDropdownTableOptions = (campaign?: Campaign): SimplePopoverPlainOption[] => [
    ...(campaign?.Status !== CampaignStatus.Active
        ? [
              {
                  label: i18n.t('viewdetails'),
                  value: DropdownOptions.ViewDetails,
              },
          ]
        : []),
    {
        label: i18n.t('copycampaign'),
        value: DropdownOptions.CopyCampaign,
    },
    ...(isCancelCampaignPossible(campaign)
        ? [
              {
                  label: i18n.t('cancelsending'),
                  value: DropdownOptions.CancelSending,
              },
          ]
        : []),
    {
        label: i18n.t('delete'),
        color: '#ff0000',
        value: DropdownOptions.Delete,
    },
];

export const mobileDropdownDataFilter = (
    options: SimplePopoverPlainOption[],
    returnOnItemClick: Campaign
): SimplePopoverPlainOption[] => getMobileDropdownTableOptions(returnOnItemClick);
